/**
 * Auth API client.
 *
 * Built on the shared `api-client.ts` so:
 *   - The base URL is centralised (and offline-friendly: returns a
 *     typed `OFFLINE` envelope instead of throwing when `VITE_API_URL`
 *     is empty).
 *   - 401s on protected calls trigger session clear automatically.
 *   - Network errors come back as a typed envelope instead of a thrown
 *     exception. Callers branch on `success`.
 *
 * Auth endpoints themselves are public (no `auth: true` flag) — login
 * and register are how a session is established. Refresh / me /
 * password-change live behind the bearer token and pass `auth: true`.
 */

import { api } from '@/shared/lib/api-client'
import { useAuthStore } from './store'
import type { AuthResponse } from './types'
import type { LoginInput, RegisterInput } from '@abdallah-issa/shared'

export function login(input: LoginInput): Promise<AuthResponse> {
  return api.post('/auth/login', input) as Promise<AuthResponse>
}

export function register(input: RegisterInput): Promise<AuthResponse> {
  return api.post('/auth/register', input) as Promise<AuthResponse>
}

/**
 * Logout: revoke the refresh token server-side (best-effort) then wipe
 * the local session. Always clears locally even if the network call
 * fails, so the user is signed out regardless.
 */
export async function logout(): Promise<void> {
  const { refreshToken, clear } = useAuthStore.getState()
  try {
    if (refreshToken) {
      await api.post('/auth/logout', { refreshToken })
    }
  } catch {
    /* ignore — we clear locally regardless */
  } finally {
    clear()
  }
}

export type { AuthResponse }
